import {
  checklistSectionSchema,
  streamerChecklistSchema,
  type ChecklistEntry,
  type ChecklistSection,
  type StreamerChecklist,
} from '@growup/streaming-shared';
import { Checkbox, cx } from '@growup/ui';
import { useState } from 'react';

import { send } from './api';
import { formatDate } from './format';
import { ProgressBar } from './ui';

const SECTION_LABEL: Record<ChecklistSection, string> = {
  welcome: 'Boas-vindas',
  padrinho: 'Conhecer o padrinho',
  brand: 'Marca Grow uP',
  technical: 'Técnica',
  streaming: 'Transmitir',
  closing: 'Fecho',
};

/** Quem marca cada item, com a cor da etiqueta (vermelho, azul, verde). */
const WHO: Record<ChecklistEntry['markedBy'], { label: string; className: string }> = {
  streamer: { label: 'Marcas tu', className: 'bg-gu-accent text-gu-on-accent' },
  padrinho: { label: 'Padrinho marca', className: 'bg-gu-info/15 text-gu-info' },
  auto: { label: 'Automático', className: 'bg-gu-success/15 text-gu-success' },
};

/** Marca (ou desfaz) um item do streamer; devolve a checklist atualizada. */
export function markChecklistEntry(entry: ChecklistEntry): Promise<StreamerChecklist> {
  return send(
    entry.done ? 'DELETE' : 'PUT',
    `/api/me/checklist/${entry.itemId}`,
    undefined,
    streamerChecklistSchema,
  );
}

const settled = (e: ChecklistEntry) => e.done || e.notApplicable;

/** Os itens pela ordem das seccoes. */
function ordered(data: StreamerChecklist): ChecklistEntry[] {
  return checklistSectionSchema.options.flatMap((section) =>
    data.entries.filter((e) => e.section === section),
  );
}

/** Os proximos itens por fazer, pela ordem das seccoes (para o Inicio). */
export function nextEntries(data: StreamerChecklist, count: number): ChecklistEntry[] {
  return ordered(data)
    .filter((e) => !settled(e))
    .slice(0, count);
}

/** Dias que faltam ate `iso` (0 se ja passou). */
export function daysUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

function WhoChip({ markedBy }: { markedBy: ChecklistEntry['markedBy'] }) {
  const who = WHO[markedBy];
  return (
    <span
      className={cx(
        'shrink-0 rounded-full px-2 py-0.5 font-sub text-caption font-bold uppercase',
        who.className,
      )}
    >
      {who.label}
    </span>
  );
}

/**
 * A checklist dos primeiros 30 dias (GS4), no separador proprio do `/painel`
 * (arrumar o painel, parte 2): resumo com barra de progresso, as seccoes como
 * passos numerados (as completas fechadas, a atual aberta), cada item com uma
 * caixa grande e uma etiqueta de quem o marca. O streamer marca os seus com um
 * clique; o proximo item por fazer fica destacado.
 */
export function ChecklistCard({
  data,
  onMark,
}: {
  data: StreamerChecklist;
  onMark: (entry: ChecklistEntry) => void;
}) {
  const [onlyMissing, setOnlyMissing] = useState(false);
  const sections = checklistSectionSchema.options
    .map((section) => ({ section, entries: data.entries.filter((e) => e.section === section) }))
    .filter((s) => s.entries.length > 0);
  const next = nextEntries(data, 1)[0] ?? null;
  const current = sections.find((s) => s.entries.some((e) => !settled(e)))?.section ?? null;
  // Abertas: a seccao atual e as que a pessoa abriu.
  const [open, setOpen] = useState<Set<ChecklistSection>>(new Set());
  const isOpen = (section: ChecklistSection) =>
    onlyMissing || section === current ? !open.has(section) : open.has(section);
  const toggle = (section: ChecklistSection) =>
    setOpen((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(section)) nextSet.delete(section);
      else nextSet.add(section);
      return nextSet;
    });
  const left = daysUntil(data.dueOn);

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2 rounded-lg border border-gu-line bg-gu-raised p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-headline">
              {data.done} de {data.total} feitos
            </h2>
            <p className="text-caption text-gu-ink-muted">
              Obrigatórios: {data.requiredDone} de {data.requiredTotal} · conversa dos 30 dias com o
              teu padrinho até {formatDate(data.dueOn)} (
              {left === 1 ? 'falta 1 dia' : `faltam ${String(left)} dias`}). Sem pressas: se
              precisares de mais tempo, ele prolonga.
            </p>
          </div>
          <div className="flex flex-wrap gap-1" aria-label="Quem marca">
            <WhoChip markedBy="streamer" />
            <WhoChip markedBy="padrinho" />
            <WhoChip markedBy="auto" />
          </div>
        </div>
        <ProgressBar done={data.done} total={data.total} />
        <Checkbox
          label="Mostrar só o que falta"
          checked={onlyMissing}
          onChange={(e) => {
            setOnlyMissing(e.target.checked);
            setOpen(new Set());
          }}
        />
      </section>

      <ol className="flex flex-col rounded-lg border border-gu-line bg-gu-raised px-4 sm:px-6">
        {sections.map(({ section, entries }, index) => {
          const doneCount = entries.filter(settled).length;
          const complete = doneCount === entries.length;
          if (onlyMissing && complete) return null;
          const shown = onlyMissing ? entries.filter((e) => !settled(e)) : entries;
          const expanded = isOpen(section);
          const counts = entries.find((e) => e.progress)?.progress;
          return (
            <li key={section} className="border-t border-gu-line py-4 first:border-t-0">
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={`passo-${section}`}
                className="flex w-full items-center gap-3 text-left"
                onClick={() => toggle(section)}
              >
                <span
                  aria-hidden="true"
                  className={cx(
                    'grid size-8 shrink-0 place-items-center rounded-full border-2 font-bold',
                    complete
                      ? 'border-gu-success bg-gu-success text-gu-ground'
                      : section === current
                        ? 'border-gu-accent text-gu-ink'
                        : 'border-gu-line text-gu-ink-muted',
                  )}
                >
                  {complete ? '✓' : index + 1}
                </span>
                <span className="flex-1 font-semibold">
                  {index + 1} · {SECTION_LABEL[section]}
                </span>
                <span className="text-caption text-gu-ink-muted">
                  {doneCount} de {entries.length}
                  {counts && !complete ? ` · ${counts}` : ''} {expanded ? '▾' : '▸'}
                </span>
              </button>
              {expanded ? (
                <ul id={`passo-${section}`} className="mt-3 flex flex-col gap-2 sm:pl-11">
                  {shown.map((e) => (
                    <ChecklistItem
                      key={e.itemId}
                      entry={e}
                      next={next?.itemId === e.itemId}
                      onMark={onMark}
                    />
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ChecklistItem({
  entry: e,
  next,
  onMark,
}: {
  entry: ChecklistEntry;
  next: boolean;
  onMark: (entry: ChecklistEntry) => void;
}) {
  const mine = e.markedBy === 'streamer';
  const box = cx(
    'mt-0.5 grid size-6 shrink-0 place-items-center rounded border-2 font-bold',
    e.done
      ? 'border-gu-success bg-gu-success text-gu-ground'
      : mine
        ? 'border-gu-ink-muted hover:border-gu-accent'
        : 'border-dashed border-gu-line',
  );
  const details = [
    e.progress,
    e.help,
    !mine && !settled(e) && e.markedBy === 'padrinho'
      ? 'Quando estiver, avisa o teu padrinho para marcar.'
      : null,
    e.done && e.doneBy && !mine
      ? `Marcado por ${e.doneBy}${e.doneAt ? ` a ${formatDate(e.doneAt)}` : ''}`
      : null,
    e.note,
  ].filter(Boolean);
  return (
    <li
      className={cx(
        'flex items-start gap-3 rounded-md bg-gu-sunken px-3 py-3',
        next && 'outline outline-2 outline-gu-accent',
      )}
    >
      {mine ? (
        <button
          type="button"
          className={box}
          aria-label={`${e.done ? 'Desfazer' : 'Feito'}: ${e.title}`}
          aria-pressed={e.done}
          onClick={() => onMark(e)}
        >
          {e.done ? '✓' : ''}
        </button>
      ) : (
        <span className={box} aria-hidden="true">
          {e.done ? '✓' : e.notApplicable ? '–' : ''}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span
            className={cx(
              'font-semibold',
              settled(e) && 'text-gu-ink-muted line-through decoration-1',
            )}
          >
            {e.title}
            {e.required ? '' : <span className="font-normal text-gu-ink-muted"> (opcional)</span>}
            {e.notApplicable ? (
              <span className="font-normal text-gu-ink-muted"> · não se aplica</span>
            ) : null}
          </span>
          <WhoChip markedBy={e.markedBy} />
        </div>
        {details.length > 0 ? (
          <span className="text-caption text-gu-ink-muted">{details.join(' · ')}</span>
        ) : null}
      </div>
    </li>
  );
}
